import {
    ChatMessageContentItem,
    FunctionCall,
    FunctionCallResponse,
} from '../../../navigation/layouts/Lori/types'

export type Topic =
    | 'inspections'
    | 'projects'
    | 'vendors'
    | 'workforce'
    | 'reports'
    | 'action_items'
    | 'change_orders'
    | 'damages'
    | 'leases'
    | 'finances'
    | 'infrastructure'
    | 'property_automation'
    | 'training'
    | 'account'
    | 'other'

export type Intent =
    | 'how_to'
    | 'create'
    | 'update'
    | 'schedule'
    | 'navigate'
    | 'troubleshoot'
    | 'support_escalation'
    | 'information'
    | 'other'

export function formatLabel(value: string): string {
    return value
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
}

export const INTENT_OPTIONS: { value: Intent; label: string }[] = [
    'how_to',
    'create',
    'update',
    'schedule',
    'navigate',
    'troubleshoot',
    'support_escalation',
    'information',
    'other',
].map((v) => ({ value: v as Intent, label: formatLabel(v) }))

export type ConversationType =
    | 'APARTMENT_WEB'
    | 'VENDOR_APP'
    | 'APARTMENT_MOBILE'

export interface TopicCount {
    topic_id: number
    label: string
    count: number
}

export interface IntentCount {
    intent: string
    label: string
    count: number
}

export interface TopicIntentCount {
    topic_id: number
    topic_label: string
    intent: string
    intent_label: string
    count: number
}

export interface OverTimePeriod {
    period: string
    count: number
}

export interface OrganizationCount {
    organization_id: number
    organization_name: string
    count: number
}

export interface WorkspaceCount {
    workspace_id: number
    workspace_name: string
    count: number
}

export interface UserCount {
    user_id: number
    user_name: string
    count: number
}

export interface AnalyticsSummary {
    total: number
    classified: number
    unclassified: number
    by_topic: TopicCount[]
    by_intent: IntentCount[]
    by_topic_and_intent: TopicIntentCount[]
    over_time: OverTimePeriod[]
    by_organization: OrganizationCount[]
    by_workspace: WorkspaceCount[]
    by_user: UserCount[]
}

export interface UserFeedback {
    id: number
    feedback: boolean
    context: string
}

export interface ChatMessageAnalytics {
    id: number
    created_date: string
    role: 'user' | 'assistant' | 'system'
    content_type:
        | 'input_text'
        | 'text'
        | 'card'
        | 'button'
        | 'error'
        | 'workorder_schedule'
        | 'highlight'
        | 'reload_data'
    message_items: ChatMessageContentItem[]
    function_call: FunctionCall | null
    function_call_response: FunctionCallResponse | null
    user_feedback: UserFeedback | null
}

export interface ConversationAnalyticsItem {
    id: number
    company: {
        id: number
        name: string
        organization: {
            id: number
            name: string
        }
    }
    user: {
        id: number
        name: string
    }
    created_date: string
    description: string
    pinned: boolean
    conversation_type: ConversationType
    platform: 'WEB' | 'MOBILE'
    topic: string | null
    topic_display: string | null
    intent: string | null
    intent_display: string | null
    chat_messages: ChatMessageAnalytics[]
}

export interface ConversationAnalyticsResponse {
    count: number
    next: string | null
    previous: string | null
    results: ConversationAnalyticsItem[]
}

export interface AnalysisFilterOption {
    id: number
    name: string
}

export interface AnalysisFilters {
    startDate: string | null
    endDate: string | null
    organizationId: number | null
    workspaceId: number | null
    userId: number | null
    topic: string | null
    intent: Intent | null
    conversationType: ConversationType | null
    excludeEzosUsers: boolean
    requireUserMessages: boolean
    groupByTime: 'month' | 'week'
}

export interface DrillStep {
    dimension: 'topic' | 'intent'
    value: string
    label: string
}

export type AnalysisView = 'dashboard' | 'conversations' | 'problems'

export const DEFAULT_FILTERS: AnalysisFilters = {
    startDate: null,
    endDate: null,
    organizationId: null,
    workspaceId: null,
    userId: null,
    topic: null,
    intent: null,
    conversationType: 'APARTMENT_WEB',
    excludeEzosUsers: true,
    requireUserMessages: true,
    groupByTime: 'month',
}

// --- Conversation Problems ---

export type ProblemCategory =
    | 'wrong_answer'
    | 'hallucination'
    | 'couldnt_complete_task'
    | 'slow_response'
    | 'wrong_language'
    | 'misunderstood_intent'
    | 'poor_sql'
    | 'other'

export const PROBLEM_CATEGORY_OPTIONS: {
    value: ProblemCategory
    label: string
}[] = [
    { value: 'wrong_answer', label: 'Wrong Answer' },
    { value: 'hallucination', label: 'Hallucination' },
    { value: 'couldnt_complete_task', label: "Couldn't Complete Task" },
    { value: 'slow_response', label: 'Slow Response' },
    { value: 'wrong_language', label: 'Wrong Language' },
    { value: 'misunderstood_intent', label: 'Misunderstood Intent' },
    { value: 'poor_sql', label: 'Poor SQL' },
    { value: 'other', label: 'Other' },
]

export interface ConversationProblem {
    id: number
    conversation: number
    conversation_detail: ConversationAnalyticsItem
    category: ProblemCategory
    category_display: string
    flagged_by: {
        id: number
        name: string
    }
    notes: string | null
    created_date: string
}

export interface ConversationProblemsResponse {
    count: number
    next: string | null
    previous: string | null
    results: ConversationProblem[]
}

export interface ProblemCategoryCount {
    category: string
    label: string
    count: number
}

export interface ProblemsSummary {
    total: number
    by_category: ProblemCategoryCount[]
    over_time: OverTimePeriod[]
}

export interface ProblemFilters {
    category: ProblemCategory | null
    startDate: string | null
    endDate: string | null
    groupByTime: 'month' | 'week'
}

export const DEFAULT_PROBLEM_FILTERS: ProblemFilters = {
    category: null,
    startDate: null,
    endDate: null,
    groupByTime: 'month',
}

// --- Heatmap Utilities ---

export interface HeatmapSeriesItem {
    x: string
    y: number
}

export interface HeatmapSeries {
    name: string
    data: HeatmapSeriesItem[]
}

export function pivotToHeatmapSeries(
    data: TopicIntentCount[],
): HeatmapSeries[] {
    const topicMap = new Map<string, Map<string, number>>()
    const allIntents = new Set<string>()

    for (const item of data) {
        allIntents.add(item.intent_label)
        if (!topicMap.has(item.topic_label)) {
            topicMap.set(item.topic_label, new Map())
        }
        topicMap.get(item.topic_label)!.set(item.intent_label, item.count)
    }

    const intentLabels = Array.from(allIntents)

    const series: HeatmapSeries[] = []
    topicMap.forEach((intentCounts, topicLabel) => {
        series.push({
            name: topicLabel,
            data: intentLabels.map((intentLabel) => ({
                x: intentLabel,
                y: intentCounts.get(intentLabel) ?? 0,
            })),
        })
    })

    return series
}
